import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const SPICES = ["Mild", "Medium", "Spicy", "Indian Spicy"];
const FORMATS = [
  { key: "thali", label: "Thali" },
  { key: "bowl", label: "Bowl" },
  { key: "solo", label: "Single Curry" },
  { key: "handi", label: "Handi", badge: "serves 1–2" },
];

/* house rule: guests never see oz portions */
export const noOz = (s) => String(s || "")
  .replace(/\b\d+(\.\d+)?\s*oz\b\.?/gi, "")
  .replace(/\(\s*\)/g, "")
  .replace(/\s*·\s*(?=·|$)/g, "")
  .replace(/\s{2,}/g, " ")
  .replace(/\s+([,.)])/g, "$1")
  .trim()
  .replace(/[·\s]+$/, "")
  .trim();

const usd = (c) => `$${(c / 100).toFixed(2)}`;

const Chip = ({ on, onClick, children, testId }) => (
  <button data-testid={testId} className={`ut-chip${on ? " on" : ""}`} onClick={onClick}>{children}</button>
);

const Section = ({ title, children }) => (
  <div style={{ marginTop: 18 }}>
    <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "#B8892B", margin: "0 0 8px" }}>{title}</p>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{children}</div>
  </div>
);

export default function BuildMeal({ table, api, go, cart, setCart }) {
  const [mb, setMb] = useState(null);
  const [err, setErr] = useState("");
  const [fmt, setFmt] = useState("thali");
  const [mains, setMains] = useState([]);
  const [size, setSize] = useState("regular");
  const [rice, setRice] = useState("");
  const [bread, setBread] = useState("");
  const [spice, setSpice] = useState("Medium");
  const [quote, setQuote] = useState(null);
  const [qErr, setQErr] = useState("");
  const [busy, setBusy] = useState(false);
  const qTimer = useRef(null);

  useEffect(() => {
    api.get("/meal-builder/menu").then((r) => setMb(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "The meal builder is unavailable right now"));
  }, [api]);

  const cfg = mb && mb[fmt];
  const need = fmt === "thali" ? (mb?.thali?.curries || 2) : 1;
  const mainsList = cfg?.mains || [];
  const riceList = (fmt === "thali" ? mb?.thali?.rices : fmt === "bowl" ? mb?.bowl?.rices : fmt === "handi" ? mb?.handi?.rices : null) || [];
  const breadList = (fmt === "thali" ? mb?.thali?.breads : fmt === "handi" ? mb?.handi?.breads : null) || [];
  const hasSize = fmt === "thali" ? !!mb?.thali?.large : fmt === "solo";

  const pickMain = (id) => {
    setMains((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (need === 1) return [id];
      return cur.length < need ? [...cur, id] : [...cur.slice(1), id];
    });
  };
  const switchFmt = (k) => { setFmt(k); setMains([]); setRice(""); setBread(""); setSize("regular"); setQuote(null); setQErr(""); };

  const selection = useMemo(() => {
    if (mains.length !== need) return null;
    const sel = { format: fmt };
    if (fmt === "thali") sel.mains = mains;
    else sel.first = mains[0]; // POS quote contract (verified live): bowl/solo/handi use `first`
    if (hasSize && size === "large") sel.size = "large";
    if (rice) sel.rice = rice;
    if (bread) sel.bread = bread;
    return sel;
  }, [fmt, mains, need, size, rice, bread, hasSize]);

  useEffect(() => {
    setQuote(null);
    setQErr("");
    if (!selection) return;
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => {
      api.post("/meal-builder/quote", { selection })
        .then((r) => { setQuote(r.data); setQErr(""); })
        .catch((e) => setQErr(e.response?.data?.detail || "Could not price this meal"));
    }, 350);
    return () => clearTimeout(qTimer.current);
  }, [selection, api]);

  const addToOrder = () => {
    if (!quote || busy) return;
    setBusy(true);
    const line = quote.lines?.[0] || {};
    const names = mainsList.filter((m) => mains.includes(m.id)).map((m) => m.name);
    const extras = [
      rice && riceList.find((r) => r.id === rice)?.name,
      bread && breadList.find((b) => b.id === bread)?.name,
    ].filter(Boolean);
    const summary = [fmt, hasSize ? (size === "large" ? "Large" : "Regular") : null, names.join(" + "),
      extras.length ? extras.join(", ") : null].filter(Boolean).join(" | ");
    const slug = `mb-${Date.now().toString(36)}`;
    setCart({
      ...cart,
      [slug]: {
        slug, qty: 1,
        name: noOz(line.name || `Build your meal (${fmt})`).slice(0, 120),
        price: (quote.total_cents || 0) / 100,
        spice,
        note: `Meal builder ${summary} | quote ${quote.quoted_at || ""}`.slice(0, 160),
      },
    });
    toast.success("Added to your order", { style: { background: "#2b2620", color: "#fff" } });
    setBusy(false);
    go("cart");
  };

  return (
    <>
      <div className="ut-topbar">
        <span className="ut-wordmark">Urban <span>Turban</span></span>
        <span className="ut-table-chip">{table ? `TABLE ${table.toUpperCase()}` : "MENU"}</span>
      </div>
      <div className="up-body" data-testid="build-meal-screen" style={{ paddingBottom: 140 }}>
        <button data-testid="build-back-btn" className="ut-back" onClick={() => go("menu")}>← Back to menu</button>
        <h1 className="ut-menu-head" style={{ marginTop: 6 }}>Build your meal</h1>
        <div className="ut-divider"><span className="ut-diamond" /></div>
        {err && <p className="up-sub" style={{ textAlign: "center" }} data-testid="build-error">{err}</p>}
        {!mb && !err && <p className="up-sub" style={{ textAlign: "center" }}>Loading the builder…</p>}
        {mb && (
          <>
            <Section title="FORMAT">
              {FORMATS.map((f) => (
                <Chip key={f.key} testId={`build-fmt-${f.key}`} on={fmt === f.key} onClick={() => switchFmt(f.key)}>
                  {f.label}{f.badge ? ` · ${f.badge}` : ""}
                </Chip>
              ))}
            </Section>
            {cfg?.includes && (
              <p className="up-sub" style={{ margin: "10px 0 0", fontSize: 12 }} data-testid="build-includes">
                {noOz(fmt === "thali" && size === "large" ? mb.thali.includes_large || cfg.includes : cfg.includes)}
              </p>
            )}
            <Section title={need === 2 ? "PICK 2 CURRIES" : "PICK YOUR CURRY"}>
              {mainsList.map((m) => (
                <Chip key={m.id} testId={`build-main-${m.id}`} on={mains.includes(m.id)} onClick={() => pickMain(m.id)}>
                  {m.veg && <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: 99, background: "#2E9E4F", marginRight: 6, verticalAlign: "middle" }} />}
                  {m.name}
                </Chip>
              ))}
            </Section>
            {hasSize && (
              <Section title="SIZE">
                <Chip testId="build-size-regular" on={size === "regular"} onClick={() => setSize("regular")}>Regular</Chip>
                <Chip testId="build-size-large" on={size === "large"} onClick={() => setSize("large")}>Large</Chip>
              </Section>
            )}
            {riceList.length > 0 && (
              <Section title={fmt === "handi" ? "RICE UPGRADE (OPTIONAL)" : "RICE"}>
                <Chip testId="build-rice-included" on={!rice} onClick={() => setRice("")}>
                  {fmt === "handi" ? "Included basmati" : "Plain (included)"}
                </Chip>
                {riceList.filter((r) => !r.plain).map((r) => (
                  <Chip key={r.id} testId={`build-rice-${r.id}`} on={rice === r.id} onClick={() => setRice(rice === r.id ? "" : r.id)}>
                    {r.name}{r.upgrade_cents ? ` +${usd(r.upgrade_cents)}` : ""}
                  </Chip>
                ))}
              </Section>
            )}
            {breadList.length > 0 && (
              <Section title={fmt === "handi" ? "NAAN UPGRADE (OPTIONAL)" : "ADD A BREAD (OPTIONAL)"}>
                <Chip testId="build-bread-none" on={!bread} onClick={() => setBread("")}>
                  {fmt === "handi" ? "Included roti" : "No bread"}
                </Chip>
                {breadList.map((b) => (
                  <Chip key={b.id} testId={`build-bread-${b.id}`} on={bread === b.id} onClick={() => setBread(bread === b.id ? "" : b.id)}>
                    {b.name}{b.upgrade_cents || b.price_cents ? ` +${usd(b.upgrade_cents ?? b.price_cents)}` : ""}
                  </Chip>
                ))}
              </Section>
            )}
            <Section title="SPICE LEVEL">
              {SPICES.map((s) => (
                <Chip key={s} testId={`build-spice-${s.toLowerCase().replace(" ", "-")}`} on={spice === s} onClick={() => setSpice(s)}>{s}</Chip>
              ))}
            </Section>
            <div className="up-card" style={{ marginTop: 22 }} data-testid="build-quote-card">
              {!selection && <p className="up-sub" style={{ margin: 0 }}>Pick {need === 2 ? "two curries" : "a curry"} to see the price</p>}
              {selection && !quote && !qErr && <p className="up-sub" style={{ margin: 0 }}>Pricing with the kitchen…</p>}
              {qErr && <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#C62828" }} data-testid="build-quote-error">{qErr}</p>}
              {quote && (
                <>
                  <p style={{ fontSize: 15, fontWeight: 700, margin: 0, color: "#3E2A1A" }} data-testid="build-quote-name">
                    {noOz(quote.lines?.[0]?.name)}
                  </p>
                  <p className="up-sub" style={{ margin: "4px 0 0", fontSize: 12 }}>
                    {(quote.lines?.[0]?.modifiers || []).map(noOz).filter(Boolean).join(" · ")} · {spice}
                  </p>
                  <p style={{ fontSize: 20, fontWeight: 800, color: "#B8892B", margin: "8px 0 0" }} data-testid="build-quote-total">
                    {quote.total || usd(quote.total_cents || 0)}
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <div className="up-bottom">
        <button data-testid="build-add-btn" className="up-btn deepred" disabled={!quote || busy} onClick={addToOrder}
          style={!quote ? { opacity: 0.5 } : undefined}>
          {quote ? `ADD TO MY ORDER — ${quote.total || usd(quote.total_cents || 0)}` : "ADD TO MY ORDER"}
        </button>
      </div>
    </>
  );
}
